import type { FC, ReactNode } from 'react'
import type { AccommodationCardData } from '@/types'

import Link from 'next/link'
import { ArrowRight, BedDouble, MapPin, Users, Waves } from 'lucide-react'
import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { formatMoney } from '@/lib/format'
import { fill, useI18n } from '@/i18n'

interface AccommodationCardProps {
    item: AccommodationCardData
    priority?: boolean
    className?: string
}

export const AccommodationCard: FC<AccommodationCardProps> = ({ item, priority = false, className }): ReactNode => {
    const { t, locale } = useI18n()
    const href = `/accommodations/${item.slug}`

    return (
        <article className={cn('group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-[0_1px_2px_rgba(19,36,58,0.05),0_18px_40px_-24px_rgba(19,36,58,0.35)] transition-shadow hover:shadow-[0_1px_2px_rgba(19,36,58,0.05),0_24px_50px_-20px_rgba(19,36,58,0.45)]', className)}>
            <div className='relative aspect-[4/3] overflow-hidden bg-sand'>
                {item.coverImage && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={item.coverImage}
                        alt={item.coverImageAlt ?? item.name}
                        loading={priority ? 'eager' : 'lazy'}
                        decoding='async'
                        className='size-full object-cover transition-transform duration-700 group-hover:scale-105'
                    />
                )}

                <div className='absolute start-4 top-4 flex flex-wrap gap-2'>
                    <span className='rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-navy shadow-sm'>{t.types[item.type]}</span>
                    {item.hasPool && (
                        <span className='flex items-center gap-1 rounded-full bg-navy/85 px-3 py-1 text-xs font-bold text-white shadow-sm'>
                            <Waves className='size-3.5' aria-hidden='true' />
                            {t.common.withPool}
                        </span>
                    )}
                </div>
            </div>

            <div className='flex flex-1 flex-col gap-3 p-6'>
                <p className='flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] text-gold uppercase'>
                    <MapPin className='size-3.5' aria-hidden='true' />
                    <span {...cms('accommodations', item.id, 'location')}>{item.location}</span>
                </p>

                <h3 className='text-2xl leading-tight font-semibold text-navy'>
                    <Link href={href} className='after:absolute after:inset-0 focus-visible:outline-none'>
                        <span {...cms('accommodations', item.id, 'name')}>{item.name}</span>
                    </Link>
                </h3>

                {item.tagline && (
                    <p className='line-clamp-2 text-sm leading-relaxed text-muted-foreground' {...cms('accommodations', item.id, item.tagline.column)}>
                        {item.tagline.value}
                    </p>
                )}

                <div className='mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 text-sm text-foreground/80'>
                    {item.maxGuests !== null && (
                        <span className='flex items-center gap-1.5'>
                            <Users className='size-4 text-gold' aria-hidden='true' />
                            {fill(t.common.guestsUpTo, { count: item.maxGuests })}
                        </span>
                    )}
                    {item.bedrooms !== null && (
                        <span className='flex items-center gap-1.5'>
                            <BedDouble className='size-4 text-gold' aria-hidden='true' />
                            {fill(t.common.bedrooms, { count: item.bedrooms })}
                        </span>
                    )}
                </div>

                <div className='flex items-end justify-between gap-3 border-t border-border pt-4'>
                    <p className='text-sm text-muted-foreground'>
                        {item.pricePerNight !== null
                            ? (
                                <>
                                    <span>{t.common.from} </span>
                                    <span className='font-heading text-2xl font-semibold text-navy'>{formatMoney(item.pricePerNight, locale)}</span>
                                    <span> {t.common.perNight}</span>
                                </>
                            )
                            : <span className='font-semibold text-navy'>{t.common.onRequest}</span>}
                    </p>

                    <span className='flex size-10 items-center justify-center rounded-full bg-sand text-navy transition-colors group-hover:bg-gold group-hover:text-gold-foreground' aria-hidden='true'>
                        <ArrowRight className='size-4 rtl:rotate-180' />
                    </span>
                </div>
            </div>
        </article>
    )
}
